/** Model sceny kreacji reklamowej. Wszystkie wymiary są w pikselach docelowego formatu. */

export type ShapeKind = 'rect' | 'ellipse' | 'triangle' | 'star' | 'line';

export type TextAlign = 'left' | 'center' | 'right';

export interface Shadow {
  color: string;
  blur: number;
  offsetX: number;
  offsetY: number;
}

interface BaseElement {
  id: string;
  /** Nazwa pokazywana na liście warstw. */
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Obrót w stopniach, wokół środka elementu. */
  rotation: number;
  opacity: number;
  locked: boolean;
  hidden: boolean;
  shadow?: Shadow;
}

export interface TextElement extends BaseElement {
  type: 'text';
  text: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  fontStyle: 'normal' | 'italic';
  color: string;
  align: TextAlign;
  /** Mnożnik wysokości wiersza (1.2 = 120% rozmiaru czcionki). */
  lineHeight: number;
  letterSpacing: number;
  uppercase: boolean;
  /** Tło pod tekstem (plakietka). Puste = brak tła. */
  background: string;
  backgroundRadius: number;
  padding: number;
  /** Obrys liter — na zdjęciach czyta się lepiej niż sam cień. */
  stroke: string;
  strokeWidth: number;
  /**
   * Zmniejsza stopień pisma tak, żeby tekst zmieścił się w ramce elementu.
   * Przy włączonym dopasowaniu wysokość ramki przestaje podążać za treścią —
   * to użytkownik ustala miejsce, a tekst się w nie wpasowuje.
   */
  autoFit: boolean;
}

export interface ShapeElement extends BaseElement {
  type: 'shape';
  shape: ShapeKind;
  fill: string;
  stroke: string;
  strokeWidth: number;
  /** Zaokrąglenie rogów, tylko dla prostokąta. */
  radius: number;
}

export interface ImageElement extends BaseElement {
  type: 'image';
  /**
   * Adres pliku w Bytescale. CDN oddaje nagłówki CORS, więc obrazek da się
   * narysować na canvasie i wyeksportować bez „skażenia" płótna.
   */
  src: string;
  radius: number;
  fit: 'cover' | 'contain';
  /** Maska koła zamiast prostokąta. */
  circle: boolean;
  /**
   * Punkt kadru przy trybie „wypełnij", 0-1 w obu osiach. Brak = środek.
   * Bez tego kadrowanie zawsze ucinało symetrycznie, więc twarz przy krawędzi
   * zdjęcia znikała.
   *
   * Pola są opcjonalne, bo dokładają się do elementów zapisanych wcześniej
   * i do gotowych szablonów, które ich nie znały.
   */
  focusX?: number;
  focusY?: number;
  /**
   * Identyfikator zasobu w banku zdjęć, gdy na planszy leży PODGLĄD ze znakiem
   * wodnym. Po nim dociągamy później licencjonowany plik.
   */
  stockId?: number;
  /** Filtry Konvy: rozmycie w px, jasność i kontrast w zakresie -1..1. */
  blur?: number;
  brightness?: number;
  contrast?: number;
  grayscale?: boolean;
}

export type AdElement = TextElement | ShapeElement | ImageElement;

export type BackgroundKind = 'solid' | 'gradient' | 'image';

export interface AdBackground {
  kind: BackgroundKind;
  color: string;
  gradientFrom: string;
  gradientTo: string;
  /** Kąt gradientu w stopniach, 0 = z lewej do prawej. */
  gradientAngle: number;
  imageSrc: string;
  /** Jak wyżej: ustawione, gdy tłem jest podgląd ze stocka. */
  imageStockId?: number;
  /** Przyciemnienie zdjęcia tła, żeby tekst pozostał czytelny. */
  overlayColor: string;
  overlayOpacity: number;
}

export interface AdScene {
  platformId: string;
  formatId: string;
  width: number;
  height: number;
  background: AdBackground;
  /** Kolejność w tablicy = kolejność rysowania, ostatni element jest na wierzchu. */
  elements: AdElement[];
}

/**
 * Tło nowej kreacji.
 *
 * Tryb `image` z pustym adresem, bo tłem kreacji reklamowej najczęściej jest
 * zdjęcie i panel ma się otwierać od razu na tej zakładce. Dopóki zdjęcia nie
 * ma, plansza rysuje się jako biała kartka: `color` jest wypełnieniem pod
 * spodem, a przyciemnienie nie wchodzi, gdy nie ma czego przykryć.
 */
export const createBackground = (): AdBackground => ({
  kind: 'image',
  color: '#ffffff',
  gradientFrom: '#0f3333',
  gradientTo: '#1a4d4d',
  gradientAngle: 135,
  imageSrc: '',
  overlayColor: '#000000',
  overlayOpacity: 0.35,
});
